// Marker icons drawn on canvas at 2× and registered with the map. One shape per
// layer; the history layer gets one circle per period colour.
import type { Map as MLMap } from "maplibre-gl";
import { LAYERS, LAYER_ORDER, PERIOD_COLORS } from "../data/layers";
import type { LayerDef } from "../data/layers";

const SIZE = 22; // CSS px
const RATIO = 2;

function draw(shape: LayerDef["shape"], color: string, stroke: string): ImageData {
  const px = SIZE * RATIO;
  const c = document.createElement("canvas");
  c.width = px; c.height = px;
  const ctx = c.getContext("2d")!;
  ctx.scale(RATIO, RATIO);
  const cx = SIZE / 2, cy = SIZE / 2, r = SIZE / 2 - 3;
  ctx.shadowColor = "rgba(0,0,0,.35)";
  ctx.shadowBlur = 3;
  ctx.shadowOffsetY = 1;
  ctx.lineJoin = "round";
  ctx.beginPath();
  switch (shape) {
    case "circle":
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      break;
    case "triangle":
      ctx.moveTo(cx, cy - r - 1);
      ctx.lineTo(cx + r + 1, cy + r - 1);
      ctx.lineTo(cx - r - 1, cy + r - 1);
      ctx.closePath();
      break;
    case "diamond":
      ctx.moveTo(cx, cy - r - 1);
      ctx.lineTo(cx + r + 1, cy);
      ctx.lineTo(cx, cy + r + 1);
      ctx.lineTo(cx - r - 1, cy);
      ctx.closePath();
      break;
    case "square": {
      const s = r * 1.7, rr = 3;
      const x = cx - s / 2, y = cy - s / 2;
      ctx.moveTo(x + rr, y);
      ctx.arcTo(x + s, y, x + s, y + s, rr);
      ctx.arcTo(x + s, y + s, x, y + s, rr);
      ctx.arcTo(x, y + s, x, y, rr);
      ctx.arcTo(x, y, x + s, y, rr);
      ctx.closePath();
      break;
    }
    case "note": {
      // hexagon
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i - Math.PI / 6;
        const x = cx + (r + 1) * Math.cos(a), y = cy + (r + 1) * Math.sin(a);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      break;
    }
    case "horseshoe": {
      // A real horseshoe: a thick arc open at the bottom, so the Horses layer is
      // unmistakable next to the hexagon and the octagon at 22 px.
      const a0 = Math.PI * 0.86, a1 = Math.PI * 2.14, ro = r + 1, ri = r * 0.46;
      ctx.arc(cx, cy + 1, ro, a0, a1);
      ctx.arc(cx, cy + 1, ri, a1, a0, true);
      ctx.closePath();
      break;
    }
    case "rail": {
      // octagon — distinct from every other shape in the set
      for (let i = 0; i < 8; i++) {
        const a = (Math.PI / 4) * i + Math.PI / 8;
        const x = cx + (r + 1) * Math.cos(a), y = cy + (r + 1) * Math.sin(a);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      break;
    }
  }
  ctx.fillStyle = color;
  ctx.fill();
  ctx.shadowColor = "transparent";
  ctx.lineWidth = 2;
  ctx.strokeStyle = stroke;
  ctx.stroke();
  if (shape === "note") {
    // a small eighth-note glyph so sessions read as "music" at a glance
    ctx.fillStyle = stroke;
    ctx.font = "bold 11px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("♪", cx, cy + 0.5);
  }
  return ctx.getImageData(0, 0, px, px);
}

export function historyIconName(period: string | undefined): string {
  return `dot-${period && PERIOD_COLORS[period] ? period : "default"}`;
}
export function layerIconName(layer: string): string {
  return `pin-${layer}`;
}
export const LIVE_TRAIN_ICON = "live-train";

/** Register every icon the overlays reference. Safe to call after each style load. */
export function addIcons(map: MLMap, theme: "light" | "dark"): void {
  // A light rim in both themes: several period colours are themselves dark, and on the
  // dark basemap a dark rim made them vanish.
  const stroke = theme === "dark" ? "#e8e6df" : "#ffffff";
  const put = (name: string, img: ImageData) => {
    if (map.hasImage(name)) map.removeImage(name);
    map.addImage(name, img, { pixelRatio: RATIO });
  };
  for (const [period, color] of Object.entries(PERIOD_COLORS)) put(`dot-${period}`, draw("circle", color, stroke));
  for (const id of LAYER_ORDER) {
    if (id === "history") continue;
    put(layerIconName(id), draw(LAYERS[id].shape, LAYERS[id].color, stroke));
  }
  // Live running trains get their own colour so they read as distinct from the
  // (static) station pins even when both layers are on.
  put(LIVE_TRAIN_ICON, draw("rail", "#e8542a", stroke));
}
